
(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    VALIDATION_ENDPOINT: window.SHOPIFY_VALIDATION_CONFIG?.endpoint || 'https://flex.p3corp.com/api/product/validate',
    REQUIRED_TAG: window.SHOPIFY_VALIDATION_CONFIG?.requiredTag || 'requires-code',
    TOKEN_EXPIRY: window.SHOPIFY_VALIDATION_CONFIG?.tokenExpiry || 10 * 60 * 1000, // 10 minutes
    MAX_VALIDATION_AGE: window.SHOPIFY_VALIDATION_CONFIG?.maxValidationAge || 60 * 60 * 1000, // 1 hour
    DEBUG: window.SHOPIFY_VALIDATION_CONFIG?.debug || false
  };

  // Secure validation state storage
  const ValidationState = (function() {
    let validations = {};
    let encryptionKey = null;

    // Generate a session-unique encryption key
    function generateKey() {
      if (!encryptionKey) {
        encryptionKey = Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map(b => b.toString(16).padStart(2, '0')).join('');
      }
      return encryptionKey;
    }

    // Simple XOR encryption for obfuscation
    function encrypt(text) {
      const key = generateKey();
      let result = '';
      for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return btoa(result);
    }

    function decrypt(encrypted) {
      try {
        const key = generateKey();
        const text = atob(encrypted);
        let result = '';
        for (let i = 0; i < text.length; i++) {
          result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
        }
        return result;
      } catch (e) {
        return null;
      }
    }

    return {
      set: function(productId, data) {
        validations[productId] = encrypt(JSON.stringify(data));
      },
      get: function(productId) {
        try {
          const decrypted = decrypt(validations[productId]);
          return decrypted ? JSON.parse(decrypted) : null;
        } catch (e) {
          return null;
        }
      },
      clear: function(productId) {
        delete validations[productId];
      },
      clearAll: function() {
        validations = {};
      }
    };
  })();

  // Utility functions
  function log(...args) {
    if (CONFIG.DEBUG) {
      console.log('[ShopifyValidation]', ...args);
    }
  }

  function generateSecureToken(code, productId) {
    const timestamp = Date.now();
    const randomBytes = Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    return btoa(`${code}-${productId}-${timestamp}-${randomBytes}`);
  }

  function isTokenValid(token, maxAge = CONFIG.MAX_VALIDATION_AGE) {
    try {
      const decoded = atob(token);
      const parts = decoded.split('-');
      if (parts.length < 3) return false;
      
      const timestamp = parseInt(parts[2]);
      return (Date.now() - timestamp) < maxAge;
    } catch (e) {
      return false;
    }
  }

  // Rate limiting
  const RateLimit = (function() {
    let attempts = {};
    
    function cleanup() {
      const now = Date.now();
      const windowMs = 15 * 60 * 1000; // 15 minutes
      
      Object.keys(attempts).forEach(ip => {
        attempts[ip] = attempts[ip].filter(time => (now - time) < windowMs);
        if (attempts[ip].length === 0) {
          delete attempts[ip];
        }
      });
    }

    return {
      isAllowed: function(identifier = 'default', maxAttempts = 5) {
        cleanup();
        
        if (!attempts[identifier]) {
          attempts[identifier] = [];
        }
        
        return attempts[identifier].length < maxAttempts;
      },
      recordAttempt: function(identifier = 'default') {
        if (!attempts[identifier]) {
          attempts[identifier] = [];
        }
        attempts[identifier].push(Date.now());
      }
    };
  })();

  // Main validation class
  class ProductValidator {
    constructor(sectionId, variantId, productId) {
      this.sectionId = sectionId;
      this.variantId = variantId;
      this.productId = productId;
      this.elements = this.getElements();
      this.bindEvents();
    }

    getElements() {
      return {
        codeInput: document.getElementById(`verify_code_${this.sectionId}`),
        redeemBtn: document.getElementById(`redeem-btn-${this.sectionId}`),
        redeemBtnText: document.getElementById(`redeem-btn-text-${this.sectionId}`),
        redeemSpinner: document.getElementById(`redeem-spinner-${this.sectionId}`),
        errorDiv: document.getElementById(`code-error-${this.sectionId}`),
        addToCartSection: document.getElementById(`add-to-cart-section-${this.sectionId}`),
        addToCartBtn: document.getElementById(`ProductSubmitButton-${this.sectionId}`),
        waitlistSection: document.getElementById('waitlist-sec'),
        redeemField: document.getElementById('redeem_field')
      };
    }

    bindEvents() {
      if (this.elements.codeInput) {
        this.elements.codeInput.addEventListener('input', this.handleInputChange.bind(this));
        this.elements.codeInput.addEventListener('keypress', this.handleKeyPress.bind(this));
      }
    }

    handleInputChange(e) {
      // Clear previous validation state when input changes
      const currentState = ValidationState.get(this.productId);
      if (currentState && currentState.code !== e.target.value.trim()) {
        this.resetValidationState();
      }
    }

    handleKeyPress(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.validateCode();
      }
    }

    async validateCode() {
      const code = this.elements.codeInput.value.trim();
      
      log('Validating code:', code, 'for product:', this.productId);
      
      this.clearError();
      
      if (!code) {
        this.showError('Please enter a redemption code');
        return;
      }

      // Check rate limiting
      const clientId = this.getClientId();
      if (!RateLimit.isAllowed(clientId)) {
        this.showError('Too many attempts. Please wait before trying again.');
        return;
      }

      this.setLoadingState(true);

      try {
        const response = await fetch(CONFIG.VALIDATION_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: code,
            productId: this.productId,
            variantId: this.variantId,
            timestamp: Date.now(),
            shopDomain: window.Shopify?.shop || window.location.hostname
          })
        });

        RateLimit.recordAttempt(clientId);
        
        const result = await response.json();
        
        if (response.ok && result.valid) {

          await this.handleValidCode(code, result);
        } else {
          // console.log(result);
          this.handleInvalidCode(result.message || 'Invalid redemption code. Please try again.');
        }
        
      } catch (error) {
        console.log('Validation error:', error);
        this.handleInvalidCode('Unable to validate code. Please check your connection and try again.');
      } finally {
        this.setLoadingState(false);
      }
    }

    async handleValidCode(code, result) {
      log('Code validated successfully');
      
      const validationData = {
        validated: true,
        code: code,
        token: result.token || generateSecureToken(code, this.productId),
        timestamp: Date.now(),
        serverToken: result.token,
        discountCode: result.discountCode || result.discount_code || code
      };

      ValidationState.set(this.productId, validationData);
      
      this.showSuccess('Code validated successfully! You can now add this item to your cart.');
      this.showAddToCartButton();
      
      // Hide waitlist section and redeem field
      if (this.elements.waitlistSection) this.elements.waitlistSection.style.display = 'none';
      if (this.elements.redeemField) this.elements.redeemField.style.display = 'none';
      
      // Update button appearance
      this.elements.redeemBtnText.textContent = 'Code Validated ✓';
      this.elements.redeemBtn.style.backgroundColor = '#008060';
      this.elements.redeemBtn.disabled = true;
      this.elements.redeemBtn.style.display = 'none';
      
      // --- START OF CHANGE ---
      // Re-find the Add to Cart button right before updating it to get the current one
      const currentAddToCartBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (currentAddToCartBtn) {
        currentAddToCartBtn.setAttribute('data-code-validated', 'true');
        currentAddToCartBtn.setAttribute('data-validation-token', validationData.token);
      } else {
        // Fallback to the initially stored reference if the new one isn't found
        this.elements.addToCartBtn.setAttribute('data-code-validated', 'true');
        this.elements.addToCartBtn.setAttribute('data-validation-token', validationData.token);
      }
      // --- END OF CHANGE ---

      const redeemCodeInput = document.getElementById(`redemption-code-input-${this.sectionId}`);
      const validationTokenInput = document.getElementById(`validation-token-input-${this.sectionId}`);
      const validationTimestampInput = document.getElementById(`validation-timestamp-input-${this.sectionId}`);

      if (redeemCodeInput) redeemCodeInput.value = code;
      if (validationTokenInput) validationTokenInput.value = validationData.token;
      if (validationTimestampInput) validationTimestampInput.value = validationData.timestamp;

      // Paid requires-code products are added immediately after successful
      // validation, then the corresponding Shopify discount code is applied.
      // Free products keep the original flow and simply reveal Add to Cart.
      if (this.isPaidProduct()) {
        await this.addPaidRedeemedItemToCart(validationData);
      }
    }

    isPaidProduct() {
      const productData = document.querySelector(
        `[data-product-id="${this.productId}"]`
      ) || document.querySelector('[data-product-id]');

      const variantPrice = Number(productData?.dataset.variantPrice || 0);
      return Number.isFinite(variantPrice) && variantPrice > 0;
    }

    async addPaidRedeemedItemToCart(validationData) {
      const addToCartBtn = document.getElementById(
        `ProductSubmitButton-${this.sectionId}`
      );
      const productForm = addToCartBtn?.closest('form[action*="/cart/add"]');

      if (!productForm) {
        throw new Error('Unable to find the product form.');
      }

      this.showSuccess('Code validated successfully. Adding your free item…');

      const formData = new FormData(productForm);

      // Preserve the existing product form data, selected variant, quantity,
      // selling plan, and all existing line-item properties.
      formData.set('properties[redemption_code]', validationData.code);

      const root = window.Shopify?.routes?.root || '/';

      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      const addResult = await addResponse.json();

      if (!addResponse.ok) {
        throw new Error(
          addResult.description ||
          addResult.message ||
          'Unable to add the redeemed item to the cart.'
        );
      }

      const discountResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          discount: validationData.discountCode
        })
      });

      const cart = await discountResponse.json();

      if (!discountResponse.ok) {
        // Avoid leaving a full-price item behind when applying the discount fails.
        const addedLineKey = addResult?.items?.[0]?.key || addResult?.key;

        if (addedLineKey) {
          await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: addedLineKey,
              quantity: 0
            })
          }).catch(() => {});
        }

        throw new Error(
          cart.description ||
          cart.message ||
          'The redemption discount could not be applied. The item was not kept in your cart.'
        );
      }

      window.location.assign(`${root}cart`);
    }

    async handleValidCodes(code, result) {
      log('Code validated successfully');
      
      const validationData = {
        validated: true,
        code: code,
        token: result.token || generateSecureToken(code, this.productId),
        timestamp: Date.now(),
        serverToken: result.token
      };

      ValidationState.set(this.productId, validationData);
      
      this.showSuccess('Code validated successfully! You can now add this item to your cart.');
      this.showAddToCartButton();
      
      // Hide waitlist section and redeem field
      if (this.elements.waitlistSection) {
        this.elements.waitlistSection.style.display = 'none';
      }
      if (this.elements.redeemField) {
        this.elements.redeemField.style.display = 'none';
      }
      
      // Update button appearance
      this.elements.redeemBtnText.textContent = 'Code Validated ✓';
      this.elements.redeemBtn.style.backgroundColor = '#008060';
      this.elements.redeemBtn.disabled = true;
      this.elements.redeemBtn.style.display = 'none';
      
      // Set validation attributes
      this.elements.addToCartBtn.setAttribute('data-code-validated', 'true');
      this.elements.addToCartBtn.setAttribute('data-validation-token', validationData.token);

      
        const redeemCodeInput = document.getElementById(`redemption-code-input-${this.sectionId}`);
        const validationTokenInput = document.getElementById(`validation-token-input-${this.sectionId}`);
        const validationTimestampInput = document.getElementById(`validation-timestamp-input-${this.sectionId}`);

        if (redeemCodeInput) redeemCodeInput.value = code;
        if (validationTokenInput) validationTokenInput.value = validationData.token;
        if (validationTimestampInput) validationTimestampInput.value = validationData.timestamp;
    }

    handleInvalidCode(message) {
      log('Code validation failed:', message);
      ValidationState.clear(this.productId);
      this.showError(message);
      this.hideAddToCartButton();
    }

    resetValidationState() {
      ValidationState.clear(this.productId);
      this.hideAddToCartButton();
      
      // Show waitlist section and redeem field again
      if (this.elements.waitlistSection) {
        this.elements.waitlistSection.style.display = 'flex';
      }
      if (this.elements.redeemField) {
        this.elements.redeemField.style.display = 'block';
      }
      
      this.elements.redeemBtnText.textContent = 'Validate Code';
      this.elements.redeemBtn.style.backgroundColor = '';
      this.elements.redeemBtn.disabled = false;
      this.elements.redeemBtn.style.display = 'inline-block';
      this.elements.addToCartBtn.setAttribute('data-code-validated', 'false');
      this.elements.addToCartBtn.setAttribute('data-validation-token', '');
    }

    setLoadingState(loading) {
      this.elements.redeemBtn.disabled = loading;
      this.elements.redeemBtnText.style.display = loading ? 'none' : 'inline';
      this.elements.redeemSpinner.classList.toggle('hidden', !loading);
    }

    showError(message) {
      this.elements.errorDiv.textContent = message;
      this.elements.errorDiv.style.color = '#d72c0d';
      this.elements.errorDiv.style.display = 'block';
    }

    showSuccess(message) {
      this.elements.errorDiv.textContent = message;
      this.elements.errorDiv.style.color = '#008060';
      this.elements.errorDiv.style.display = 'block';
    }

    clearError() {
      this.elements.errorDiv.style.display = 'none';
      this.elements.errorDiv.textContent = '';
    }

    showAddToCartButton() {
      this.elements.addToCartSection.style.display = 'block';
    }

    hideAddToCartButton() {
      this.elements.addToCartSection.style.display = 'none';
    }

    getClientId() {
      // Create a semi-persistent client identifier
      let clientId = sessionStorage.getItem('validation_client_id');
      if (!clientId) {
        clientId = 'client_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('validation_client_id', clientId);
      }
      return clientId;
    }
  }

  // Check if product has required tag
  function productRequiresCode() {
    const productData = document.querySelector('[data-product-tags]');
    if (!productData) return false;
    
    const tags = productData.dataset.productTags.split(',').map(tag => tag.trim().toLowerCase());
    return tags.includes(CONFIG.REQUIRED_TAG.toLowerCase());
  }

  // Form submission interceptor
function interceptFormSubmissions() {
  document.addEventListener('submit', function(e) {
    const form = e.target;
    
    if (!form.matches('form[action*="/cart/add"]')) return;
    
    const addToCartBtn = form.querySelector('[data-code-validated]');
    if (!addToCartBtn) return;
    
    // Check if this is a restricted product that needs validation
    if (addToCartBtn.dataset.codeValidated !== 'true') {
      e.preventDefault();
      alert('Please validate your redemption code before adding this item to cart.');
      return false;
    }
    
    // Add validation data to form
    const token = addToCartBtn.dataset.validationToken;
    const productId = getCurrentProductId();
    const validationData = ValidationState.get(productId);
    
    if (validationData) {
      // Add as line item properties
      // addHiddenInput(form, 'properties[validation_token]', token);
      // addHiddenInput(form, 'properties[redemption_code]', validationData.code);
      // addHiddenInput(form, 'properties[_validation_timestamp]', Date.now().toString());
      // addHiddenInput(form, 'properties[_requires_code]', 'true');
      
      // Also add to cart attributes (order level)
      // addHiddenInput(form, 'attributes[redemption_code]', validationData.code);
    //   addHiddenInput(form, 'attributes[validation_token]', token);
    //   addHiddenInput(form, 'attributes[validation_timestamp]', Date.now().toString());
    }
  });
}


  function addHiddenInput(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  // Cart API interceptor
  function interceptCartAPI() {
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const [url, options] = args;
      
      if (url.includes('/cart/add') && options?.method === 'POST') {
        return validateCartAddRequest(originalFetch, url, options);
      }
      
      return originalFetch.apply(this, args);
    };
  }

async function validateCartAddRequest(originalFetch, url, options) {
  try {
    const requestData = await extractRequestData(options.body);
    
    if (requestData && requiresCodeValidation(requestData)) {
      if (!hasValidValidation(requestData)) {
        throw new Error('This product requires a valid redemption code.');
      }
      
      // Add validation data for API requests
      if (options.body instanceof FormData) {
        const productId = getCurrentProductId();
        const validationData = ValidationState.get(productId);
        
        if (validationData) {
          options.body.append('properties[redemption_code]', validationData.code);
          // options.body.append('properties[validation_token]', validationData.token);
          // options.body.append('properties[validation_timestamp]', Date.now().toString());
        }
      } else if (typeof options.body === 'string') {
        try {
          const body = JSON.parse(options.body);
          const productId = getCurrentProductId();
          const validationData = ValidationState.get(productId);
          
          if (validationData) {
            if (!body.properties) body.properties = {};
            if (!body.properties) body.properties = {};
            body.properties.redemption_code = validationData.code;
            // body.properties.validation_token = validationData.token;
            // body.properties.validation_timestamp = Date.now().toString();
            
            options.body = JSON.stringify(body);
          }
        } catch (e) {
          // Handle URL encoded data
          const params = new URLSearchParams(options.body);
          const productId = getCurrentProductId();
          const validationData = ValidationState.get(productId);
          
          if (validationData) {
            params.append('properties[redemption_code]', validationData.code);
            // params.append('properties[validation_token]', validationData.token);
            // params.append('properties[validation_timestamp]', Date.now().toString());
            options.body = params.toString();
          }
        }
      }
    }
    
    return originalFetch(url, options);
  } catch (error) {
    return Promise.reject(error);
  }
}

  async function extractRequestData(body) {
    if (body instanceof FormData) {
      return Object.fromEntries(body.entries());
    } else if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (e) {
        const params = new URLSearchParams(body);
        return Object.fromEntries(params.entries());
      }
    }
    return null;
  }

  function requiresCodeValidation(requestData) {
    // Check if the request contains the requires_code property
    return requestData['properties[_requires_code]'] === 'true' || 
           productRequiresCode();
  }

  function hasValidValidation(requestData) {
    // console.log(requestData, requestData['attributes[redemption_code]'])
    if (!requestData['properties[redemption_code]']) {
        console.log("Cannot find code")
      return false;
    }  else {
        return true
    }
    
    // const token = requestData['attributes[validation_token]'];
    // return isTokenValid(token);
  }

  function getCurrentProductId() {
    const productData = document.querySelector('[data-product-id]');
    return productData?.dataset.productId || 'unknown';
  }

  // DOM manipulation protection
  function setupDOMProtection() {
    if (typeof MutationObserver === 'undefined') return;
    
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.target.hasAttribute('data-code-validated')) {
          const target = mutation.target;
          
          if (target.getAttribute('data-code-validated') === 'true' && !isValidationLegitimate(target)) {
            log('Detected illegitimate validation manipulation');
            target.setAttribute('data-code-validated', 'false');
            target.setAttribute('data-validation-token', '');
            
            const sectionId = target.id.split('-').pop();
            const addToCartSection = document.getElementById(`add-to-cart-section-${sectionId}`);
            if (addToCartSection) {
              addToCartSection.style.display = 'none';
            }
          }
        }
      });
    });
    
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-code-validated', 'data-validation-token']
    });
  }

  function isValidationLegitimate(element) {
    const productId = getCurrentProductId();
    const storedValidation = ValidationState.get(productId);
    
    if (!storedValidation?.validated) return false;
    
    const validationAge = Date.now() - storedValidation.timestamp;
    return validationAge <= CONFIG.TOKEN_EXPIRY;
  }

  // Public API
  window.ShopifyProductValidation = {
    init: function(sectionId, variantId, productId) {
      return new ProductValidator(sectionId, variantId, productId);
    },
    
    configure: function(config) {
      Object.assign(CONFIG, config);
    },
    
    clearValidation: function(productId) {
      if (productId) {
        ValidationState.clear(productId);
      } else {
        ValidationState.clearAll();
      }
    },
    
    getValidationState: function(productId) {
      return ValidationState.get(productId);
    }
  };

  // Auto-initialization
  document.addEventListener('DOMContentLoaded', function() {
    log('Initializing Shopify Product Validation');
    
    // Only initialize if product requires code validation
    if (productRequiresCode()) {
      interceptFormSubmissions();
      interceptCartAPI();
      setupDOMProtection();
      
      // Auto-init validators for products with validation elements
      document.querySelectorAll('[data-product-id]').forEach(element => {
        const productId = element.dataset.productId;
        const variantId = element.dataset.variantId;
        const sectionId = element.dataset.sectionId || 'default';
        
        window.ShopifyProductValidation.init(sectionId, variantId, productId);
      });
    }
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', function() {
    ValidationState.clearAll();
  });

})();